// MLA 9 and APA 7 formatting for the Citations tool and the "Cite this page" sheet. Pure string
// work: nothing is fetched and nothing is stored here.
//
// Italics are carried as a marker (U+2063 around the italic part) so a preview can show them while
// copied plain text stays clean. Show a citation with toHtml (escaped) or build nodes from parts();
// copy it with toPlain.

const MARK = "\u2063";
const has = (v) => v != null && String(v).trim().length > 0;
const dot = (s) => (/[.!?]$/.test(String(s).trim()) ? String(s).trim() : String(s).trim() + ".");

export function italic(s) { return MARK + String(s).trim() + MARK; }
export function toPlain(s) { return String(s).split(MARK).join(""); }
export function toHtml(s, esc) { return esc(String(s)).split(MARK).map((seg, i) => (i % 2 ? "<i>" + seg + "</i>" : seg)).join(""); }
// [{ text, italic }] for building DOM nodes with textContent.
export function parts(s) { return String(s).split(MARK).map((text, i) => ({ text, italic: i % 2 === 1 })).filter((p) => p.text); }

// "Last, First" -> "Last, F." (APA keeps initials; MLA keeps the name as typed).
function apaAuthor(a) {
	if (!has(a)) return "";
	const m = a.split(",");
	if (m.length < 2) return dot(a);
	const last = m[0].trim();
	const first = m[1].trim();
	const initials = first.split(/\s+/).filter(Boolean).map((w) => w[0].toUpperCase() + ".").join(" ");
	return last + ", " + initials;
}

export function mla(type, f) {
	const parts = [];
	if (has(f.author)) parts.push(dot(f.author));
	if (type === "website") {
		if (has(f.title)) parts.push("“" + f.title.trim() + ".”");
		if (has(f.site)) parts.push(italic(f.site) + ",");
		const date = [f.month, f.year].filter(has).map((x) => x.trim()).join(" ");
		if (date) parts.push(date + ",");
		if (has(f.url)) parts.push(f.url.trim().replace(/^https?:\/\//, "") + ".");
		if (has(f.accessed)) parts.push("Accessed " + f.accessed.trim() + ".");
	} else if (type === "book") {
		if (has(f.title)) parts.push(italic(f.title) + ".");
		if (has(f.publisher)) parts.push(f.publisher.trim() + ",");
		if (has(f.year)) parts.push(f.year.trim() + ".");
	} else {
		if (has(f.title)) parts.push("“" + f.title.trim() + ".”");
		if (has(f.journal)) {
			let j = italic(f.journal);
			if (has(f.volume)) j += ", vol. " + f.volume.trim();
			if (has(f.issue)) j += ", no. " + f.issue.trim();
			if (has(f.year)) j += ", " + f.year.trim();
			if (has(f.pages)) j += ", pp. " + f.pages.trim();
			parts.push(j + ".");
		}
	}
	return parts.join(" ").replace(/\s+/g, " ").trim();
}

export function apa(type, f) {
	const parts = [];
	const auth = apaAuthor(f.author);
	if (type === "website") {
		// No author: the title takes the author's place. No date: (n.d.).
		const date = "(" + (has(f.year) ? f.year.trim() : "n.d.") + ").";
		if (auth) parts.push(auth, date);
		if (has(f.title)) parts.push(dot(f.title));
		if (!auth) parts.push(date);
		if (has(f.site)) parts.push(italic(f.site) + ".");
		if (has(f.url)) parts.push(f.url.trim());
		return has(f.title) || auth ? parts.join(" ").replace(/\s+/g, " ").trim() : "";
	}
	if (auth) parts.push(auth);
	if (has(f.year)) parts.push("(" + f.year.trim() + ").");
	if (type === "book") {
		if (has(f.title)) parts.push(italic(f.title) + ".");
		if (has(f.publisher)) parts.push(dot(f.publisher));
	} else {
		if (has(f.title)) parts.push(dot(f.title));
		if (has(f.journal)) {
			let j = italic(f.journal);
			if (has(f.volume)) j += ", " + italic(f.volume);
			if (has(f.issue)) j += "(" + f.issue.trim() + ")";
			if (has(f.pages)) j += ", " + f.pages.trim();
			parts.push(j + ".");
		}
	}
	return parts.join(" ").replace(/\s+/g, " ").trim();
}

export function format(style, type, f) { return (style === "apa" ? apa : mla)(type, f || {}); }

// MLA abbreviates months longer than four letters.
const MLA_MONTHS = ["Jan.", "Feb.", "Mar.", "Apr.", "May", "June", "July", "Aug.", "Sept.", "Oct.", "Nov.", "Dec."];
export function mlaDate(d) { return d.getDate() + " " + MLA_MONTHS[d.getMonth()] + " " + d.getFullYear(); }

// No control, bidi, zero-width or line separator characters; one line, capped. Page titles are
// written by the page, so they are only ever display text.
// eslint-disable-next-line no-control-regex
const HIDDEN = /[\u0000-\u001f\u007f-\u009f\u061c\u200b-\u200f\u2028-\u202e\u2060-\u2069\ufeff]/g;
export function cleanLine(s, max = 300) {
	return String(s == null ? "" : s).replace(HIDDEN, "").replace(/\s+/g, " ").trim().slice(0, max);
}

// The website fields for a page the student has open: only its title and address (the tab's own
// state), the site's host name and today's date. Author and year are left for the student to check.
export function pageFields(title, url, now = new Date()) {
	let u = null;
	try { u = new URL(String(url)); } catch { u = null; }
	const ok = !!u && (u.protocol === "http:" || u.protocol === "https:");
	return {
		author: "",
		title: cleanLine(title, 300),
		site: ok ? u.hostname.replace(/^www\./, "") : "",
		year: "",
		month: "",
		url: ok ? cleanLine(u.href, 2000) : "",
		accessed: mlaDate(now),
	};
}

// The fields a saved citation may keep (revault:citations entries' `f`), as strings.
export const WEBSITE_KEYS = ["author", "title", "site", "year", "month", "url", "accessed"];
export function pickFields(f) {
	const out = {};
	for (const k of WEBSITE_KEYS) out[k] = cleanLine(f && f[k], k === "url" ? 2000 : 300);
	return out;
}
